import * as _ from "lodash-es";
import type { PartialDeep } from "type-fest";
import {
	computed,
	nextTick,
	onScopeDispose,
	provide,
	ref,
	watch,
	type ComputedRef,
	type InjectionKey,
	type Ref,
} from "vue";
import {
	useFieldFactory,
	useFieldNestedFactory,
	type FieldFactory,
	type FieldNestedFactory,
} from "./field";
import {
	createInternalFormApi,
	type ErrorsBag,
	type FormOption,
	type GenericValues,
} from "./form-context";
import type { MaybeRef, Path } from "./types";
import { injectWithSelf, safeClone } from "./utils";

type FieldMetasBag<TValues extends GenericValues> = {
	[K in Path<TValues>]?: any;
};

type BeforeSubmitCallback<TValues extends GenericValues> = (values: TValues) => TValues | void;

export type ValidationResult<TValues extends GenericValues> =
	| {
			success: true;
			data: TValues;
	  }
	| {
			success: false;
			errors: ErrorsBag<TValues>;
	  };

type HandleSubmit<T> = <R>(cb: (v: T) => R) => (event?: Event | unknown) => Promise<R | undefined>;
export type PublicFormApi<TValues extends GenericValues> = {
	/**
	 * A readonly copy of the form value state
	 */
	values: ComputedRef<TValues>;

	errors: ErrorsBag<TValues>;
	metas: FieldMetasBag<TValues>;
	valid: Ref<boolean>;
	/**
	 * Remove all metedata AND the state from the form
	 */
	clear: () => Promise<void>;
	getValues: () => TValues;
	handleSubmit: HandleSubmit<TValues>;

	submitting: Ref<boolean>;

	/**
	 * Register an event handler that will be called right after the validation, but juste before the submit
	 * This function may return a modified form values and it will be passed to handleSubmit.
	 */
	onBeforeSubmit: (cb: BeforeSubmitCallback<TValues>) => void;
	setValues: (values: PartialDeep<TValues>) => void;
	useField: FieldFactory<TValues>;
	validateAll: () => Promise<ValidationResult<TValues>>;
	useNestedField: FieldNestedFactory<TValues>;
	wheneverIsValid: (cb: (v: TValues) => void) => void;
};

export type FormApiId<TValues extends GenericValues> = InjectionKey<PublicFormApi<TValues>>;

const FORM_PUBLIC_ID: FormApiId<any> = Symbol();

export function useForm<TValues extends GenericValues = GenericValues>(
	options?: MaybeRef<FormOption>
): PublicFormApi<TValues> {
	const [internalId, api] = createInternalFormApi<TValues>(options || {});

	const valid = computed(() => {
		const { metas } = api.state;
		for (const path of Object.keys(metas) as Path<TValues>[]) {
			if (!metas[path]?.valid) {
				return false;
			}
		}

		return true;
	});

	function _clearObject(obj: Record<string, any>) {
		for (const key of Object.getOwnPropertyNames(obj)) {
			delete obj[key];
		}
	}

	async function clear() {
		const { fields } = api.state;

		for (const field of fields.values()) {
			field.clear();
		}
	}

	function mergeState<T>(state: T, values: PartialDeep<T>) {
		return _.mergeWith(state, safeClone(values), (_, v) => {
			if (v === undefined) {
				return null;
			}

			return v;
		});
	}

	function setValues(values: PartialDeep<TValues>) {
		mergeState(api.state.values, safeClone(values));
	}

	function getValues(): TValues {
		return safeClone(api.values.value);
	}

	function setAllFormTouched() {
		for (const path of Object.keys(api.state.metas) as Path<TValues>[]) {
			if (api.state.metas[path]) {
				api.state.metas[path]!.touched = true;
			}
		}
	}

	async function validateAll(): Promise<ValidationResult<TValues>> {
		setAllFormTouched();

		let valid = true;

		for (const field of api.state.fields.values()) {
			const fieldValid = await field.validate();
			if (!fieldValid) {
				valid = false;
			}
		}

		if (valid) {
			return {
				success: true,
				data: getValues(),
			};
		} else {
			return {
				success: false,
				errors: api.state.errors,
			};
		}
	}

	const beforeSubmits = new Set<BeforeSubmitCallback<TValues>>();
	function onBeforeSubmit(cb: BeforeSubmitCallback<TValues>) {
		beforeSubmits.add(cb);

		onScopeDispose(() => {
			beforeSubmits.delete(cb);
		});
	}

	const submitting = ref(false);

	function handleSubmit<R>(cb: (v: TValues) => R) {
		return async (event?: Event | unknown) => {
			try {
				submitting.value = true;
				if (event && event instanceof Event) {
					event.preventDefault();
				}

				if (!api.submittedOnce.value) {
					api.submittedOnce.value = true;
				}

				const result = await validateAll();
				if (!result.success) {
					return;
				}

				let values = result.data;
				for (const cb of beforeSubmits) {
					const updatedValues = cb(values);
					if (updatedValues) {
						values = updatedValues;
					}
				}

				const out = await cb(values);
				return out;
			} finally {
				submitting.value = false;
			}
		};
	}

	function wheneverIsValid(cb: (v: TValues) => void) {
		watch(getValues, async (values) => {
			await nextTick();
			if (!valid.value) return;
			cb(values);
		});
	}

	const publicApi: PublicFormApi<TValues> = {
		values: api.values,
		errors: api.state.errors,
		metas: api.state.metas,
		valid,
		clear,
		setValues,
		getValues,
		handleSubmit,
		onBeforeSubmit,
		submitting,
		validateAll,
		useField: useFieldFactory(internalId),
		useNestedField: useFieldNestedFactory(internalId),
		wheneverIsValid,
	};

	provide(FORM_PUBLIC_ID, publicApi);

	return publicApi;
}

export function useFormContext<TValues extends GenericValues>(): PublicFormApi<TValues> {
	const context = injectWithSelf(FORM_PUBLIC_ID);
	if (!context) throw new Error("[Form] Public form api not found");

	return context;
}
